/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/

import React, { useEffect, useState, useRef, useCallback } from "react";
import Topheader from "../../../Layout/header/header";
import {
  Backdrop,
  Button,
  CircularProgress,
  Toolbar,  
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  InputAdornment,
  Checkbox,
  Snackbar,
  Alert,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useNavigate, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import EnglishLanguage from "./EnglishLanguage.jsx";
import { useSelector, useDispatch } from "react-redux";
import { fetchGlobalAdminCategoriesDetails } from "../../../Services/redux/global-admin-users/categories/GlobalAdminCategoriesActions.js";
import APIManager from "../../../Services/ApiManager/ApiManager";

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const AddCategories = ({ isDomainApprover = false }) => {
  const readOnly_GlobalAdmin =
    sessionStorage.getItem("readonly_GlobalAdmin") === "true" ||
    (isDomainApprover && sessionStorage.getItem("readonly_DomainApprover") === "true");
  const hasInitializedFormData = useRef(false);
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loader, setLoader] = useState(false);
  const [categoryDetails, setCategoryDetails] = useState({});
  const [domains, setDomains] = useState([]);
  const [domainsList, setDomainsList] = useState([]);
  const [domainDAId, setDomainDAId] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [openUpdateConfirmDialog, setOpenUpdateConfirmDialog] = useState(false);
  const [originalLanguageFormData, setOriginalLanguageFormData] = useState({
    "en-US": {
      name: "",
      description: "",
      icon: null,
      domain: "",
    },
  });

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    icon: null,
    domain: "",
  });
              
  const globalCategories = useSelector(
    (state) => state.globalAdminCategories.categories
  );

  const [value, setValue] = useState(0);
  const [languageTabs, setLanguageTabs] = useState([
    { label: "English (Default)", code: "en-US", isDefault: true },
  ]);
  const [openLanguageModal, setOpenLanguageModal] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [languageTab, setLanguageTab] = useState(0);
  const [includeAll, setIncludeAll] = useState(false);

  const [languageFormData, setLanguageFormData] = useState({
    "en-US": {
      name: "",
      description: "",
      icon: null,
      domain: "",
    },
  });
  const [currentLanguageCode, setCurrentLanguageCode] = useState("en-US");
  const [languageValidationState, setLanguageValidationState] = useState({
    "en-US": false,
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const handleChange = (event, newValue) => {
    setLanguageFormData((prev) => ({
      ...prev,
      [currentLanguageCode]: {
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        domain: formData.domain,
      },
    }));

    setValue(newValue);
    const newLanguageCode = languageTabs[newValue].code;
    setCurrentLanguageCode(newLanguageCode);
    if (languageFormData[newLanguageCode]) {
      setFormData(languageFormData[newLanguageCode]);
    } else {
      setFormData({
        name: "",
        description: "",
        icon: languageFormData["en-US"].icon,
        domain: languageFormData["en-US"].domain,
      });
    }
  };

  const handleAddLanguageClick = async () => {
    try {
      setLoader(true);
      const languagesData = await APIManager.getLanguages("en-001");
      if (languagesData && Array.isArray(languagesData)) {
        setAvailableLanguages(languagesData);
        if (selectedLanguages.length === 0) {
          const defaultEnglish = languagesData.find(
            (lang) =>
              lang.languageTag === "en-US" ||
              (lang.defaultName === "English" &&
                lang.defaultCountry === "United States")
          );
          if (defaultEnglish) {
            setSelectedLanguages([defaultEnglish]);
          }
        }
      }
      setLoader(false);
      setOpenLanguageModal(true);
    } catch (error) {
      console.error("Failed to fetch languages:", error);
      setLoader(false);
    }
  };

  const handleCloseLanguageModal = () => {
    setOpenLanguageModal(false);
    setSearchQuery("");
    setLanguageTab(0);
    setIncludeAll(false);
  };

  const handleLanguageTabChange = (event, newValue) => {
    setLanguageTab(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleAddLanguage = (language) => {
    if (
      !selectedLanguages.some(
        (lang) => lang.languageTag === language.languageTag
      )
    ) {
      setSelectedLanguages([...selectedLanguages, language]);
    }
  };

  const handleRemoveLanguage = (languageTag) => {
    setSelectedLanguages(
      selectedLanguages.filter((lang) => lang.languageTag !== languageTag)
    );
    setIncludeAll(false);
  };

  const handleIncludeAllChange = (event) => {
    const isChecked = event.target.checked;
    setIncludeAll(isChecked);

    if (isChecked) {
      const allLanguages = [...availableLanguages];
      const defaultLang = allLanguages.find(
        (lang) => lang.languageTag === "en-US"
      );
      if (defaultLang) {
        const otherLangs = allLanguages.filter(
          (lang) => lang.languageTag !== "en-US"
        );
        setSelectedLanguages([defaultLang, ...otherLangs]);
      } else {
        setSelectedLanguages(allLanguages);
      }
    } else {
      const defaultLang = selectedLanguages.find(
        (lang) => lang.languageTag === "en-US"
      );
      if (defaultLang) {
        setSelectedLanguages([defaultLang]);
      } else {
        const firstLang =
          selectedLanguages.length > 0 ? selectedLanguages[0] : null;
        if (firstLang) {
          setSelectedLanguages([firstLang]);
        } else {
          setSelectedLanguages([]);
        }
      }
    }
  };

  const handleAddSelectedLanguages = () => {
    // First, save the current language data
    const updatedCurrentLanguageData = {
      ...languageFormData,
      [currentLanguageCode]: {
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        domain: formData.domain,
      },
    };

    const newLanguageTabs = selectedLanguages.map((lang, index) => {
      const isDefault = index === 0 || lang.languageTag === "en-US";

      if (isDefault) {
        return {
          label: `${lang.defaultName} (Default)`,
          code: lang.languageTag,
          isDefault: true,
        };
      }

      return {
        label: `${lang.defaultName}${
          lang.defaultCountry ? ` (${lang.defaultCountry})` : ""
        }`,
        code: lang.languageTag,
        isDefault: false,
      };
    });

    if (newLanguageTabs.length > 0) {
      setLanguageTabs(newLanguageTabs);
      const updatedLanguageFormData = { ...updatedCurrentLanguageData };
      const defaultLangData = updatedLanguageFormData["en-US"];
      const newValidationState = { ...languageValidationState };

      newLanguageTabs.forEach((tab) => {
        if (!updatedLanguageFormData[tab.code]) {
          updatedLanguageFormData[tab.code] = {
            name: "",
            description: "",
            icon: defaultLangData?.icon || null,
            domain: defaultLangData?.domain || "",
          };
          newValidationState[tab.code] = false;
        }
      });
      setLanguageFormData(updatedLanguageFormData);
      setLanguageValidationState(newValidationState);
    }

    handleCloseLanguageModal();
  };

  const isLanguageSelected = (languageTag) => {
    return selectedLanguages.some((lang) => lang.languageTag === languageTag);
  };

  const isDefaultLanguage = (languageTag) => {
    return (
      languageTag === "en-US" ||
      (selectedLanguages.length > 0 &&
        selectedLanguages[0].languageTag === languageTag)
    );
  };

  const filteredAvailableLanguages = availableLanguages.filter((lang) => {
    const searchLower = searchQuery.toLowerCase();
    const displayName = `${lang.defaultName}${
      lang.defaultCountry ? ` (${lang.defaultCountry})` : ""
    }`.toLowerCase();
    const languageTag = lang.languageTag.toLowerCase();
    const matchesSearch =
      displayName.includes(searchLower) || languageTag.includes(searchLower);
    const notSelected = !isLanguageSelected(lang.languageTag);

    return matchesSearch && notSelected;
  });

  const filteredSelectedLanguages = selectedLanguages.filter((lang) => {
    const searchLower = searchQuery.toLowerCase();
    const displayName = `${lang.defaultName}${
      lang.defaultCountry ? ` (${lang.defaultCountry})` : ""
    }`.toLowerCase();
    const languageTag = lang.languageTag.toLowerCase();
    return (
      displayName.includes(searchLower) || languageTag.includes(searchLower)
    );
  });

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDeleteSuccess = (categoryName, errorMessage) => {
    if (errorMessage) {
      setSnackbarMessage(errorMessage);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } else {
      setSnackbarMessage(`Category - ${categoryName} deleted successfully!`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    }
  };

  const categoriesNavTarget = isDomainApprover
    ? { path: "/ui/domain-approver/portfolio-settings", state: { activeMenu: "Categories" } }
    : { path: "/ui/harman-admin/categories" };

  const navigateToCategories = () => {
    navigate(categoriesNavTarget.path, categoriesNavTarget.state ? { state: categoriesNavTarget.state } : undefined);
  };

  const handleBackClick = () => {
    navigateToCategories();
  };

  useEffect(() => {
    dispatch(fetchGlobalAdminCategoriesDetails());
  }, [dispatch]);

  useEffect(() => { 
    if (isDomainApprover) {
      const domainFromSession = sessionStorage.getItem("domain") || "";
      setDomains([domainFromSession]);
      setDomainsList([{ domainAlias: domainFromSession }]);
      setFormData((prev) => ({ ...prev, domain: domainFromSession }));
      setLanguageFormData((prev) => ({
        ...prev,
        "en-US": { ...prev["en-US"], domain: domainFromSession },
      }));

      const fetchDomainApproverAliasId = async () => {
        try {
          const aliasData = await APIManager.getDomainApproverAliasID();
          if (aliasData?.domainAliasId) {
            setDomainDAId(aliasData.domainAliasId);
          }
        } catch (error) {
          console.error("AddCategories - Failed to fetch domain approver alias ID:", error);
        }
      };
      fetchDomainApproverAliasId();
    } else {
      const fetchDomains = async () => {
        try {
          const domainsData = await APIManager.getAdminDomain();
          if (domainsData && Array.isArray(domainsData)) {
            setDomainsList(domainsData);
            const domainAliases = domainsData.map((domain) => domain.domainAlias);
            setDomains(domainAliases);
          }
        } catch (error) {
          console.error("AddCategories - Failed to fetch domains:", error);
        }
      };
      fetchDomains();
    }
  }, [isDomainApprover]);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setLoader(true);
        const languagesData = await APIManager.getLanguages("en-001");
        if (languagesData && Array.isArray(languagesData)) {
          setAvailableLanguages(languagesData);
          const defaultEnglish = languagesData.find(
            (lang) =>
              lang.languageTag === "en-US" ||
              (lang.defaultName === "English" &&
                lang.defaultCountry === "United States")
          );
          if (defaultEnglish) {
            setSelectedLanguages([defaultEnglish]);
          }
        }
      } catch (error) {
        console.error("AddCategories - Failed to fetch languages:", error);
      } finally {
        setLoader(false);
      }
    };

    fetchLanguages();
  }, []);

  const buildInitialLanguageFormData = (categoryData) => {
    const initialIconSrc = categoryData.icon?.thumbnailUrl || categoryData.icon?.url || null;
    const original = {
      name: categoryData.translatedNames?.["en-US"] || "",
      description: categoryData.descriptionForLocale?.["en-US"] || "",
      icon: initialIconSrc,
      domain: categoryData.domain || "",
    };
    const result = {};
    if (categoryData.translatedNames) {
      Object.keys(categoryData.translatedNames).forEach((langCode) => {
        result[langCode] = {
          name: categoryData.translatedNames[langCode] || "",
          description: categoryData.descriptionForLocale?.[langCode] || "",
          icon: initialIconSrc,
          domain: categoryData.domain || "",
        };
      });
    }
    if (!result["en-US"]) {
      result["en-US"] = original;
    }
    return result;
  };

  const buildValidationState = (languageFormDataMap) => {
    const state = {};
    Object.keys(languageFormDataMap).forEach((langCode) => {
      const langData = languageFormDataMap[langCode];
      state[langCode] =
        langData.name.trim() !== "" &&
        langData.description.trim() !== "" &&
        langData.icon !== null &&
        langData.domain !== "";
    });
    return state;
  };

  const buildTabsAndSelectedLanguages = (languageCodes, availableLangs) => {
    const loadedLanguageTabs = [];
    const loadedSelectedLanguages = [];
    const sortedCodes = [...languageCodes].sort((a, b) => {
      if (a === "en-US") return -1;
      if (b === "en-US") return 1;
      return 0;
    });
    sortedCodes.forEach((langCode, index) => {
      const langInfo = availableLangs.find((l) => l.languageTag === langCode);
      if (langInfo) {
        const isDefault = index === 0 || langCode === "en-US";
        const countrySuffix = langInfo.defaultCountry ? ` (${langInfo.defaultCountry})` : "";
        const label = isDefault
          ? `${langInfo.defaultName} (Default)`
          : `${langInfo.defaultName}${countrySuffix}`;
        loadedLanguageTabs.push({
          label,
          code: langCode,
          isDefault,
        });
        loadedSelectedLanguages.push(langInfo);
      } else {
        loadedLanguageTabs.push({
          label: langCode === "en-US" ? `${langCode} (Default)` : langCode,
          code: langCode,
          isDefault: langCode === "en-US",
        });
      }
    });
    return { loadedLanguageTabs, loadedSelectedLanguages };
  };

  useEffect(() => {
    try {
      if (!categoryId) return;
      const categoryData = globalCategories.find((cat) => cat.id === categoryId);
      setCategoryDetails(categoryData);
      if (!categoryData) return;

      const initialLanguageFormData = buildInitialLanguageFormData(categoryData);
      setLanguageFormData(initialLanguageFormData);
      setOriginalLanguageFormData(structuredClone(initialLanguageFormData));

      if (!hasInitializedFormData.current && initialLanguageFormData["en-US"]) {
        setFormData(initialLanguageFormData["en-US"]);
        hasInitializedFormData.current = true;
      }

      setLanguageValidationState(buildValidationState(initialLanguageFormData));

      const languageCodes = Object.keys(initialLanguageFormData);
      if (languageCodes.length > 0 && availableLanguages.length > 0) {
        const { loadedLanguageTabs, loadedSelectedLanguages } =
          buildTabsAndSelectedLanguages(languageCodes, availableLanguages);

        if (loadedLanguageTabs.length > 0) {
          setLanguageTabs((prev) => {
            const byCode = new Map();
            [...prev, ...loadedLanguageTabs].forEach((tab) => {
              if (tab?.code) byCode.set(tab.code, tab);
            });
            return Array.from(byCode.values());
          });
        }

        if (loadedSelectedLanguages.length > 0) {
          setSelectedLanguages((prev) => {
            const map = new Map();
            [...prev, ...loadedSelectedLanguages].forEach((lang) => {
              if (lang?.languageTag) map.set(lang.languageTag, lang);
            });
            return Array.from(map.values());
          });
        }
      }
    } catch (err) {
      console.log(err);
    }
  }, [categoryId, globalCategories, availableLanguages]);
  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      return updated;
    });

    setLanguageFormData((prev) => ({
      ...prev,
      [currentLanguageCode]: {
        ...prev[currentLanguageCode],
        [field]: value,
      },
    }));

    if (field === "domain" || field === "icon") {
      setLanguageFormData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((langCode) => {
          updated[langCode] = {
            ...updated[langCode],
            [field]: value,
          };
        });
        return updated;
      });
    }
  };

  useEffect(() => {
    if (categoryId && originalLanguageFormData) {
      const originalDataForLang = originalLanguageFormData[currentLanguageCode];

      if (originalDataForLang) {
        const nameChanged = formData.name !== originalDataForLang.name;
        const descriptionChanged =
          formData.description !== originalDataForLang.description;
        const domainChanged = formData.domain !== originalDataForLang.domain;
        const iconChanged = formData.icon instanceof File;

        const hasAnyChange =
          nameChanged || descriptionChanged || domainChanged || iconChanged;
        setHasChanges(hasAnyChange);
      } else {
        const hasName = formData.name && formData.name.trim() !== "";
        const hasDescription =
          formData.description && formData.description.trim() !== "";
        const hasAnyEdit = hasName || hasDescription;
        setHasChanges(hasAnyEdit);
      }
    } else if (!categoryId) {
      setHasChanges(true);
    }
  }, [formData, originalLanguageFormData, categoryId, currentLanguageCode]);

  const areAllLanguagesValid = () => {
    return languageTabs.every((tab) => {
      return languageValidationState[tab.code] === true;
    });
  };

  const handleFormValidation = useCallback((isValid) => {
    setLanguageValidationState((prev) => ({
      ...prev,
      [currentLanguageCode]: isValid,
    }));
  }, [currentLanguageCode]);

  const handleSaveClick = () => {
    setLanguageFormData((prev) => ({
      ...prev,
      [currentLanguageCode]: {
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        domain: formData.domain,
      },
    }));

    if (categoryId) {
      setOpenUpdateConfirmDialog(true);
    } else {
      handleSaveCategory();
    }
  };

  const handleCloseUpdateConfirmDialog = () => {
    setOpenUpdateConfirmDialog(false);
  };

  const handleConfirmUpdate = () => {
    setOpenUpdateConfirmDialog(false);
    handleSaveCategory();
  };

  const showErrorSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarSeverity("error");
    setSnackbarOpen(true);
  };

  const extractErrorMessage = (error, fallback) => {
    if (!error.message) return fallback;
    try {
      const cleanedMessage = error.message.replaceAll('<EOL>', "\n");
      const jsonMatch = cleanedMessage.match(/\[\s*\{[\s\S]*?\}\s*\]/);
      if (jsonMatch) {
        const errorArray = JSON.parse(jsonMatch[0]);
        if (Array.isArray(errorArray) && errorArray.length > 0 && errorArray[0].message) {
          return errorArray[0].message;
        }
      }
      return error.message;
    } catch {
      return error.message;
    }
  };

  const buildLocaleData = (finalLanguageFormData) => {
    const translatedNames = {};
    const descriptionForLocale = {};
    Object.keys(finalLanguageFormData).forEach((langCode) => {
      const langData = finalLanguageFormData[langCode];
      if (langData.name?.trim()) {
        translatedNames[langCode] = langData.name.trim();
      }
      if (langData.description?.trim()) {
        descriptionForLocale[langCode] = langData.description.trim();
      }
    });
    return { translatedNames, descriptionForLocale };
  };

  const uploadIconIfNeeded = async (icon) => {
    if (!(icon instanceof File)) return categoryDetails.icon;
    const iconResponse = await APIManager.postCategoryIcon(categoryId, icon);
    return iconResponse;
  };

  const handleUpdateCategory = async (finalLanguageFormData, defaultLangData, selectedDomainObj) => {
    let updatedIconData;
    try {
      updatedIconData = await uploadIconIfNeeded(defaultLangData.icon);
    } catch (iconError) {
      console.error("Failed to upload icon:", iconError);
      showErrorSnackbar("Failed to upload icon. Please try again.");
      return;
    }

    const { translatedNames, descriptionForLocale } = buildLocaleData(finalLanguageFormData);
    const categoryPayload = {
      id: categoryId,
      translatedNames,
      descriptionForLocale,
      domain: defaultLangData.domain,
      domainAliasId: selectedDomainObj.domainAliasId,
      parentCategoryId: categoryDetails.parentCategoryId || null,
      hasConnectedApps: categoryDetails.hasConnectedApps || false,
      hasSubCategories: categoryDetails.hasSubCategories || false,
    };

    if (updatedIconData) {
      categoryPayload.icon = {
        id: updatedIconData.id,
        fileName: updatedIconData.fileName,
        heightInPx: updatedIconData.heightInPx,
        sizeInBytes: updatedIconData.sizeInBytes,
        widthInPx: updatedIconData.widthInPx,
        createdAt: updatedIconData.createdAt,
        lastModified: updatedIconData.lastModified,
        sha256Hash: updatedIconData.sha256Hash,
        url: updatedIconData.url,
        thumbnailUrl: updatedIconData.thumbnailUrl,
      };
    }

    await APIManager.putUpdateCategory(categoryId, categoryPayload);
    setSnackbarMessage("Category updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    dispatch(fetchGlobalAdminCategoriesDetails());
    setTimeout(() => navigateToCategories(), 1500);
  };

  const handleCreateCategory = async (finalLanguageFormData, defaultLangData, selectedDomainObj) => {
    const { translatedNames, descriptionForLocale } = buildLocaleData(finalLanguageFormData);
    const categoryPayload = {
      id: "",
      translatedNames,
      descriptionForLocale,
      domainAliasId: selectedDomainObj.domainAliasId,
    };

    const formDataToSend = new FormData();
    formDataToSend.append("file", defaultLangData.icon);
    formDataToSend.append("category", JSON.stringify(categoryPayload));

    await APIManager.postCreateCategory(formDataToSend);
    setSnackbarMessage("Category saved successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    dispatch(fetchGlobalAdminCategoriesDetails());
    setTimeout(() => navigateToCategories(), 1500);
  };

  const handleSaveCategory = async () => {
    try {
      setLoader(true);
      const finalLanguageFormData = {
        ...languageFormData,
        [currentLanguageCode]: {
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          domain: formData.domain,
        },
      };
      const defaultLangData = finalLanguageFormData["en-US"];

      if (!defaultLangData.name || !defaultLangData.description || !defaultLangData.domain) {
        showErrorSnackbar("Please fill in all required fields for the default language (English)");
        return;
      }

      if (!categoryId && !defaultLangData.icon) {
        showErrorSnackbar("Please upload a category icon");
        return;
      }

      let domainAliasId;
      if (isDomainApprover) {
        if (!domainDAId) {
          showErrorSnackbar("Failed to fetch domain alias ID. Please try again.");
          return;
        }
        domainAliasId = domainDAId;
      } else {
        const selectedDomainObj = domainsList.find((d) => d.domainAlias === defaultLangData.domain);
        if (!selectedDomainObj?.domainAliasId) {
          showErrorSnackbar("Invalid domain selected. Please try again.");
          return;
        }
        domainAliasId = selectedDomainObj.domainAliasId;
      }

      const domainObj = { domainAliasId };
      if (categoryId) {
        await handleUpdateCategory(finalLanguageFormData, defaultLangData, domainObj);
      } else {
        await handleCreateCategory(finalLanguageFormData, defaultLangData, domainObj);
      }
    } catch (error) {
      console.error("Save category error:", error);
      const fallback = `Failed to ${categoryId ? "update" : "create"} category. Please try again.`;
      showErrorSnackbar(extractErrorMessage(error, fallback));
    } finally {
      setLoader(false);
    }
  };

  return (
    <>
      <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <Toolbar />

      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          minHeight: "calc(100vh - 64px)",
          padding: "0 32px",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: "1100px",
            display: "flex",
            flexDirection: "row",
            gap: "24px",
          }}
        >
          <Box
            sx={{
              width: "295px",
              height: "auto",
              maxHeight: "134px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              padding: "24px 0px 24px 0px",
            }}
          >
            <Box
              sx={{
                width: "100%",
                height: "auto",
                maxWidth: "138px",
                maxHeight: "36px",
                borderRadius: "4px",
                padding: "10px 14px",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
                marginLeft: "0px",
              }}
            >
              <ArrowBackIcon
                onClick={handleBackClick}
                sx={{
                  cursor: "pointer",
                  height: "14px",
                  width: "14px",
                  color: "#12141599",
                }}
              />
              <Typography
                sx={{
                  fontWeight: 500,
                  width: "88px",
                  height: "16px",
                  textWrap: "nowrap",
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#2A2C2D",
                }}
              >
                All Categories
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: "860px",
              display: "flex",
              flexDirection: "column",
              gap: "24px",
              padding: "24px 0px",
            }}
          >
            <Box
              sx={{
                width: "inherit",
                maxWidth: "860px",
                display: "flex",
                flexDirection: "column",
                gap: "24px",
              }}
            >
              <Box
                sx={{
                  width: "100%",
                  maxWidth: "860px",
                  height: "auto",
                  maxHeight: "32px",
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    width: "100%",
                    maxWidth: categoryId ? "auto" : "117px",
                    height: "auto",
                    maxHeight: "29px",
                    fontWeight: 500,
                    fontSize: "24px",
                    lineHeight: "120%",
                    letterSpacing: "0%",
                    color: "#121415",
                    textWrap: "nowrap",
                  }}
                >
                  {categoryId && categoryDetails?.translatedNames?.["en-US"]
                    ? categoryDetails.translatedNames["en-US"]
                    : "New Category"}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "8px",
                    width: "100%",
                    maxWidth: "236px",
                    height: "auto",
                    maxHeight: "32px",
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      width: "auto",
                      height: "auto",
                      maxHeight: "32px",
                      minWidth: "114px",
                      padding: "8px 12px",
                      textTransform: "none",
                      borderRadius: "4px",
                      color: "#2A2C2D",
                      backgroundColor: "#1214151A",
                    }}
                    onClick={handleBackClick}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!areAllLanguagesValid() || !hasChanges}
                    sx={{
                      width: "auto",
                      height: "auto",
                      maxHeight: "32px",
                      minWidth: "114px",
                      padding: "8px 12px",
                      textTransform: "none",
                      borderRadius: "4px",
                      color: "#FFFFFF",
                      backgroundColor: "#0476E2",
                    }}
                    onClick={handleSaveClick}
                  >
                    Save
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  maxWidth: "860px",
                  height: "auto",
                  minHeight: "663px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "32px",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: "860px",
                    height: "auto",
                    minHeight: "44px",
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Typography
                    sx={{
                      width: "inherit",
                      height: "24px",
                      fontWeight: 500,
                      fontSize: "18px",
                      lineHeight: "24px",
                      letterSpacing: "0.15px",
                      color: "#000000DE",
                      textWrap: "nowrap",
                    }}
                  >
                    Name & Translations
                  </Typography>
                  <Typography
                    sx={{
                      fontWeight: "400",
                      fontSize: "12px",
                      lineHeight: "16px",
                      letterSpacing: "0.4px",
                      textWrap: "nowrap",
                      color: "#00000080",
                    }}
                  >
                    Maximum 40 characters. Name shall be unique per language.{" "}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: "inherit",
                    height: "auto",
                    maxHeight: "32px",
                    display: "flex",
                    gap: "16px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexGrow: 1,
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Tabs
                        value={value}
                        onChange={handleChange}
                        aria-label="language tabs"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{maxWidth: "680px"}}
                      >
                        {languageTabs.map((tab, index) => (
                          <Tab
                            key={tab.code}
                            label={tab.label}
                            {...a11yProps(index)}
                            sx={{
                              textTransform: "none",
                            }}
                          />
                        ))}
                      </Tabs>
                    </Box>
                  </Box>

                  <Button
                    onClick={handleAddLanguageClick}
                    sx={{
                      width: "100%",
                      maxWidth: "137px",
                      height: "auto",
                      maxHeight: "32px",
                      borderRadius: "4px",
                      textWrap: "nowrap",
                      textTransform: "none",
                      padding: "8px 12px",
                      border: "1px solid #12141599",
                      color: "#2A2C2D",
                      backgroundColor: "#FFFFFF",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "#F5F5F5",
                      },
                    }}
                    variant="contained"
                    startIcon={<AddIcon />}
                    color="primary"
                    disabled={readOnly_GlobalAdmin}
                  >
                    Add Language
                  </Button>
                </Box>

                <Box>
                  {languageTabs.map((tab, index) => (
                    <CustomTabPanel key={tab.code} value={value} index={index}>
                      <EnglishLanguage
                        categoryDetails={categoryDetails}
                        showDelete={!!categoryId}
                        onFormDataChange={handleFieldChange}
                        onValidationChange={handleFormValidation}
                        domains={domains}
                        languageCode={tab.code}
                        languageName={tab.label}
                        formData={formData}
                        onDeleteSuccess={handleDeleteSuccess}
                        isDomainApprover={isDomainApprover}
                      />
                    </CustomTabPanel>
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog
        open={openLanguageModal}
        onClose={handleCloseLanguageModal}
        maxWidth="sm"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: "580px",
              maxWidth: "580px",
              height: "512px",
              borderRadius: "8px",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 24px",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "20px",
              lineHeight: "24px",
              color: "#121415",
            }}
          >
            Add Language
          </Typography>
          <IconButton
            onClick={handleCloseLanguageModal}
            sx={{
              padding: "4px",
            }}
          >
            <CloseIcon sx={{ fontSize: "20px", color: "#12141599" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "16px 24px",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search Language"
            value={searchQuery}
            onChange={handleSearchChange}
            variant="outlined"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#12141599", fontSize: "20px" }} />
                  </InputAdornment>
                ),
                sx: {
                  height: "40px",
                  borderRadius: "4px",
                  fontSize: "14px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E0E0E0",
                  },
                },
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: "40px",
              },
            }}
          />
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={languageTab}
              onChange={handleLanguageTabChange}
              sx={{
                minHeight: "32px",
                "& .MuiTab-root": {
                  minHeight: "32px",
                  textTransform: "none",
                  fontSize: "14px",
                  fontWeight: 500,
                  padding: "6px 12px",
                },
              }}
            >
              <Tab
                label={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    <span style={{ fontWeight: "bold" }}>Available</span>
                    <Box
                      sx={{
                        backgroundColor: "#0476E2",
                        color: "#FFFFFF",
                        borderRadius: "10px",
                        padding: "2px 8px",
                        fontSize: "12px",
                        fontWeight: 600,
                        minWidth: "24px",
                        textAlign: "center",
                      }}
                    >
                      {filteredAvailableLanguages.length}
                    </Box>
                  </Box>
                }
              />
              <Tab
                label={
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "4px" }}
                  >
                    <span style={{ fontWeight: "bold", color: "#000000" }}>
                      Selected
                    </span>
                    <Box
                      sx={{
                        backgroundColor: "#686565ff",
                        color: "#FFFFFF",
                        borderRadius: "10px",
                        padding: "2px 8px",
                        fontSize: "12px",
                        fontWeight: 600,
                        minWidth: "24px",
                        textAlign: "center",
                      }}
                    >
                      {selectedLanguages.length}
                    </Box>
                  </Box>
                }
              />
            </Tabs>
          </Box>
          <Box
            sx={{
              maxHeight: "240px",
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#C4C4C4",
                borderRadius: "3px",
              },
            }}
          >
            {(() => {
              const availableLanguagesContent =
                filteredAvailableLanguages.length > 0 ? (
                  filteredAvailableLanguages.map((lang) => (
                    <Box
                      key={lang.languageTag}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr auto",
                        gap: "16px",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: "#F5F5F5",
                        borderRadius: "4px",
                      }}
                    >
                      {/* Left Column - Language Name */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#2A2C2D",
                          }}
                        >
                          {lang.defaultName}
                          {lang.defaultCountry ? ` (${lang.defaultCountry})` : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#12141599",
                          }}
                        >
                          {lang.languageTag}
                        </Typography>
                      </Box>
                      {/* Right Column - Add Button */}
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          onClick={() => handleAddLanguage(lang)}
                          sx={{
                            padding: "4px",
                            color: includeAll ? "#12141533" : "#0476E2",
                          }}
                          disabled={
                            includeAll ||
                            selectedLanguages.some(
                              (selected) =>
                                selected.languageTag === lang.languageTag
                            )
                          }
                        >
                          <AddCircleOutlineIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography
                    sx={{
                      textAlign: "center",
                      color: "#12141599",
                      padding: "20px",
                    }}
                  >
                    No languages found
                  </Typography>
                );

              const selectedLanguagesContent =
                filteredSelectedLanguages.length > 0 ? (
                  filteredSelectedLanguages.map((lang) => (
                    <Box
                      key={lang.languageTag}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr auto",
                        gap: "16px",
                        alignItems: "center",
                        padding: "8px 12px",
                        backgroundColor: "#F5F5F5",
                        borderRadius: "4px",
                      }}
                    >
                      {/* Left Column - Language Name */}
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "2px",
                          minWidth: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: isDefaultLanguage(lang.languageTag)
                              ? "#12141599"
                              : "#2A2C2D",
                          }}
                        >
                          {lang.defaultName}
                          {lang.defaultCountry ? ` (${lang.defaultCountry})` : ""}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 400,
                            color: "#12141599",
                          }}
                        >
                          {lang.languageTag}
                        </Typography>
                      </Box>
                      {/* Right Column - Remove Button */}
                      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <IconButton
                          onClick={() => handleRemoveLanguage(lang.languageTag)}
                          disabled={isDefaultLanguage(lang.languageTag)}
                          sx={{
                            padding: "4px",
                            color: isDefaultLanguage(lang.languageTag)
                              ? "#12141533"
                              : "#2A2C2D",
                            "&:disabled": {
                              color: "#12141533",
                            },
                          }}
                        >
                          <RemoveCircleOutlineIcon sx={{ fontSize: "20px" }} />
                        </IconButton>
                      </Box>
                    </Box>
                  ))
                ) : (
                  <Typography
                    sx={{
                      textAlign: "center",
                      color: "#12141599",
                      padding: "20px",
                    }}
                  >
                    No languages selected
                  </Typography>
                );

              return languageTab === 0 ? availableLanguagesContent : selectedLanguagesContent;
            })()}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            padding: "16px 24px",
            borderTop: "1px solid #E0E0E0",
            gap: "8px",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <Checkbox
              checked={includeAll}
              onChange={handleIncludeAllChange}
              sx={{
                padding: "4px",
                "& .MuiSvgIcon-root": {
                  fontSize: "20px",
                },
              }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 400,
                color: "#2A2C2D",
              }}
            >
              Include All
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: "8px" }}>
            <Button
              onClick={handleCloseLanguageModal}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                padding: "8px 16px",
                height: "36px",
                minWidth: "114px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#2A2C2D",
                borderColor: "#C4C4C4",
                backgroundColor: "#EEEEEE",
                "&:hover": {
                  borderColor: "#C4C4C4",
                  backgroundColor: "#EEEEEE",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddSelectedLanguages}
              variant="contained"
              disabled={selectedLanguages.length === 0 && !includeAll}
              sx={{
                textTransform: "none",
                borderRadius: "4px",
                padding: "8px 16px",
                height: "36px",
                minWidth: "114px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#FFFFFF",
                backgroundColor: "#0476E2",
                "&:hover": {
                  backgroundColor: "#0360B8",
                },
                "&:disabled": {
                  backgroundColor: "#E0E0E0",
                  color: "#12141599",
                },
              }}
            >
              Add
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openUpdateConfirmDialog}
        onClose={handleCloseUpdateConfirmDialog}
        maxWidth="sm"
        slotProps={{
          paper: {
            sx: {
              width: "420px",
              maxWidth: "420px",
              borderRadius: "0px",
              padding: "24px",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "0 0 16px 0",
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "24px",
              color: "#D32F2F",
            }}
          >
            Update Category - {formData.name || "ZooTesting123"}
          </Typography>
          <IconButton
            onClick={handleCloseUpdateConfirmDialog}
            sx={{
              padding: "4px",
            }}
          >
            <CloseIcon sx={{ fontSize: "20px", color: "#12141599" }} />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            padding: "0 0 24px 0",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px",
              color: "#2A2C2D",
            }}
          >
            The category will be updated.
          </Typography>
          <Typography
            sx={{
              fontSize: "16px",
              fontWeight: 400,
              lineHeight: "24px",
              color: "#2A2C2D",
            }}
          >
            That action can not be undone.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            padding: "0",
            gap: "8px",
            justifyContent: "flex-end",
          }}
        >
          <Button
            onClick={handleCloseUpdateConfirmDialog}
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              padding: "8px 24px",
              height: "40px",
              minWidth: "114px",
              fontSize: "14px",
              fontWeight: 500,
              color: "#0476E2",
              borderColor: "#0476E2",
              backgroundColor: "#FFFFFF",
              "&:hover": {
                borderColor: "#0476E2",
                backgroundColor: "#F5F5F5",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            sx={{
              textTransform: "none",
              borderRadius: "20px",
              padding: "8px 24px",
              height: "40px",
              minWidth: "114px",
              fontSize: "14px",
              fontWeight: 500,
              color: "#FFFFFF",
              backgroundColor: "#0476E2",
              "&:hover": {
                backgroundColor: "#0360B8",
              },
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

AddCategories.propTypes = {
  isDomainApprover: PropTypes.bool,
};

export default AddCategories;
